import React, { useMemo } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LegendItem, MONO_FONT, headCellSx, numCellSx, textCellSx } from "../../components/metrics/metricsUi";
import { inventoryRiskService } from "../../utils/services/inventoryRiskService";
import { purchaseOrderAnalysisService } from "../../utils/services/purchaseOrderAnalysisService";
import { RISK_BY_KEY } from "../../utils/inventoryRiskPageDefaults";
import {
  COVERAGE_BY_KEY,
  LONG_STOCKOUT,
  RISK_ROW_LABEL,
  STAGE_LABEL,
  TIMING_LABEL,
  coverageColor,
  coverageSentence,
  fmtCompact,
  fmtDay,
  fmtDays,
  fmtLead,
  fmtMoney,
  fmtPct,
  fmtShortDay,
  fmtUnits,
  riskColor,
} from "../../utils/purchaseOrderAnalysisPageDefaults";
import { Stat, StatusChip, eyebrowSx, tooltipContentStyle, useLatestRequest } from "./parts";

const SERIES = {
  in_hand: "On hand (daily snapshot)",
  projected: "Projected, with open POs",
  projected_no_inbound: "Projected, if nothing arrives",
  arrival_units: "PO arriving (units)",
};

/** Past snapshots + forward projection on one date axis (one units scale). */
const mergeRunway = (history, projection) => {
  const byDate = new Map();
  (history?.series || []).forEach((point) => {
    byDate.set(point.date, { date: point.date, in_hand: point.in_hand });
  });
  (projection?.series || []).forEach((point) => {
    const row = byDate.get(point.date) || { date: point.date };
    row.projected = point.projected;
    row.projected_no_inbound = point.projected_no_inbound;
    row.arrival_units = point.arrival_units;
    row.arrival_pos = point.arrival_pos;
    byDate.set(point.date, row);
  });
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
};

const RunwayTooltip = ({ active, payload, label, ui }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <Box sx={{ ...tooltipContentStyle(ui), p: 1.25 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 650, mb: 0.25 }}>{fmtDay(label)}</Typography>
      {row.in_hand != null ? <Typography sx={{ fontSize: 12 }}>On hand {fmtUnits(row.in_hand)}</Typography> : null}
      {row.projected != null ? <Typography sx={{ fontSize: 12 }}>With open POs {fmtUnits(row.projected)}</Typography> : null}
      {row.projected_no_inbound != null ? (
        <Typography sx={{ fontSize: 12, color: ui.colors.textSecondary }}>If nothing arrives {fmtUnits(row.projected_no_inbound)}</Typography>
      ) : null}
      {row.arrival_units ? (
        <Typography sx={{ fontSize: 12, fontWeight: 650 }}>
          +{fmtUnits(row.arrival_units)} arriving · {(row.arrival_pos || []).join(", ")}
        </Typography>
      ) : null}
    </Box>
  );
};

const Runway = ({ ui, detail, history }) => {
  const { colors } = ui;
  const data = useMemo(() => mergeRunway(history, detail.projection), [history, detail.projection]);
  const onHandColor = ui.accent;
  const noInboundColor = colors.textTertiary;
  const arrivalColor = coverageColor("covered", ui.darkMode);
  const stockoutColor = RISK_BY_KEY.out_of_stock.color;
  const projection = detail.projection;

  if (!projection) {
    return (
      <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, py: 2 }}>
        No sales at this warehouse in the last 30 days (nor in the 180 days before), so there is no rate to project.
      </Typography>
    );
  }
  return (
    <>
      <Box sx={{ height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 14, right: 10, left: -6, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={colors.border} />
            <XAxis dataKey="date" tickFormatter={fmtShortDay} minTickGap={28} tick={{ fontSize: 10.5, fill: colors.textSecondary }} tickLine={false} axisLine={{ stroke: colors.border }} />
            <YAxis tickFormatter={fmtCompact} allowDecimals={false} tick={{ fontSize: 10.5, fill: colors.textSecondary }} tickLine={false} axisLine={false} width={48} />
            <RechartsTooltip content={<RunwayTooltip ui={ui} />} />
            <ReferenceLine
              x={detail.today}
              stroke={projection.stockout_date === detail.today ? stockoutColor : colors.textSecondary}
              label={{
                value: projection.stockout_date === detail.today ? "Today · out of stock" : "Today",
                position: "top",
                fontSize: 10.5,
                // Label text stays in text ink; the line carries the stock-out colour.
                fill: projection.stockout_date === detail.today ? colors.textPrimary : colors.textSecondary,
              }}
            />
            {projection.stockout_date && projection.stockout_date !== detail.today ? (
              <ReferenceLine
                x={projection.stockout_date}
                stroke={stockoutColor}
                strokeWidth={1.5}
                label={{ value: "Stock-out", position: "insideTopRight", fontSize: 10.5, fill: colors.textPrimary }}
              />
            ) : null}
            <Bar dataKey="arrival_units" name={SERIES.arrival_units} fill={arrivalColor} maxBarSize={10} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Line dataKey="in_hand" name={SERIES.in_hand} stroke={onHandColor} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
            <Line dataKey="projected_no_inbound" name={SERIES.projected_no_inbound} stroke={noInboundColor} strokeWidth={2} strokeDasharray="2 4" dot={false} isAnimationActive={false} />
            <Line dataKey="projected" name={SERIES.projected} stroke={onHandColor} strokeWidth={2} strokeDasharray="6 4" dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
        <LegendItem color={onHandColor} label={SERIES.in_hand} line />
        <LegendItem color={onHandColor} label={`${SERIES.projected} (dashed)`} line />
        <LegendItem color={noInboundColor} label={SERIES.projected_no_inbound} line />
        <LegendItem color={arrivalColor} label={SERIES.arrival_units} />
      </Box>
      <Typography sx={{ fontSize: 11.5, color: colors.textTertiary, mt: 0.75, lineHeight: 1.5 }}>
        Projection = on hand falling at the {detail.position.risk === LONG_STOCKOUT ? "last known" : "30-day"} sales rate ({fmtUnits(detail.position.daily_velocity * 7)}/week), stepping up on
        each scheduled PO arrival. Overdue, undated and stale POs are not drawn — they have no date to plot.
        {projection.stockout_date
          ? ` Runs out ${fmtDay(projection.stockout_date)}${
              projection.stockout_date !== projection.stockout_date_no_inbound
                ? ` (${fmtDay(projection.stockout_date_no_inbound)} without the open POs)`
                : ""
            }.`
          : " Does not run out in this window."}
      </Typography>
    </>
  );
};

const HISTORY_COLUMNS = ["PO", "Warehouse", "PO date", "Ordered", "Received", "Expected", "First receipt", "Lead", "Late", "Status"];

/**
 * One SKU at the selected warehouse: position, PO coverage, stock runway and
 * every PO that ever carried it (any warehouse, any age).
 */
const SkuDetail = ({ ui, sku, base, onOpenPo }) => {
  const { colors } = ui;
  const detailState = useLatestRequest(
    sku && base.warehouse_id
      ? (signal) => purchaseOrderAnalysisService.fetchSku({ ...base, sku }, { signal })
      : null,
    [sku, base.warehouse_id, base.client_id, base.months],
  );
  const detail = detailState.data && detailState.data.sku === sku ? detailState.data : null;
  const productId = detail?.product_id;
  const historyState = useLatestRequest(
    productId && detail?.position
      ? (signal) =>
          inventoryRiskService.fetchHistory(
            {
              warehouse_id: base.warehouse_id,
              product_id: productId,
              sku,
              days: 30,
              ...(base.client_id ? { client_id: base.client_id } : {}),
            },
            { signal },
          )
      : null,
    [productId, sku, base.warehouse_id, base.client_id, Boolean(detail?.position)],
  );

  if (detailState.error) return <Typography sx={{ color: ui.danger, fontSize: 13 }}>{detailState.error}</Typography>;
  if (!detail) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: colors.textSecondary, fontSize: 13, py: 2 }}>
        <CircularProgress size={16} /> Loading {sku}…
      </Box>
    );
  }

  const { position, coverage, stats } = detail;
  const longOut = position?.risk === LONG_STOCKOUT;
  const risk = position ? (longOut ? { short: RISK_ROW_LABEL[LONG_STOCKOUT] } : RISK_BY_KEY[position.risk]) : null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, flexWrap: "wrap", mb: 1.5 }}>
        <Typography sx={{ fontFamily: MONO_FONT, fontSize: 17, fontWeight: 700, color: colors.textPrimary }}>{sku}</Typography>
        <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>{detail.product_name || ""}</Typography>
      </Box>

      <Box sx={{ ...ui.card, p: 2, display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(5, minmax(0, 1fr))" }, gap: 1.5, mb: 1.5 }}>
        <Stat ui={ui} label="On hand" value={position ? fmtUnits(position.on_hand) : "—"} sub={detail.warehouse?.name} />
        <Stat
          ui={ui}
          label="30-day sales"
          value={position ? fmtUnits(position.units_30d) : "—"}
          sub={
            longOut
              ? `was ${fmtUnits(position.last_known_units_30d)}/30d to ${fmtShortDay(position.last_sale_date)}`
              : position
                ? fmtMoney(position.revenue_30d)
                : null
          }
        />
        <Stat
          ui={ui}
          label="Days cover"
          value={position ? (position.risk === "out_of_stock" || longOut ? "Out" : fmtDays(position.days_cover)) : "—"}
          sub={risk ? (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: riskColor(position.risk) }} />
              {risk.short}
            </Box>
          ) : null}
        />
        <Stat ui={ui} label="Inbound here" value={fmtUnits(detail.inbound.filter((line) => line.state !== "unconfirmed").reduce((sum, line) => sum + line.units, 0))} sub={`${fmtUnits(detail.inbound.length)} open PO lines`} />
        <Stat ui={ui} label="Lead time" value={fmtLead(stats.median_lead_days)} sub={`this SKU · on time ${fmtPct(stats.on_time_pct)}`} />
      </Box>

      {coverage ? (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", px: 1.5, py: 1, mb: 2, borderRadius: "10px", backgroundColor: ui.surfaceAlt, flexWrap: "wrap" }}>
          <StatusChip ui={ui} strong color={coverageColor(coverage.status, ui.darkMode)} label={COVERAGE_BY_KEY[coverage.status]?.short} />
          <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>
            {coverageSentence(coverage)}
            {coverage.suggested_qty > 0 ? ` Suggested order: ${fmtUnits(coverage.suggested_qty)} units.` : ""}
          </Typography>
        </Box>
      ) : position ? (
        <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, mb: 2 }}>
          Not at risk within the {detail.horizon.days}-day horizon — no inbound decision needed yet.
        </Typography>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, mb: 2 }}>
          No stock or recent sales for this SKU at this warehouse.
        </Typography>
      )}

      {position ? (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ ...eyebrowSx(colors), mb: 0.5 }}>Stock runway</Typography>
          <Runway ui={ui} detail={detail} history={historyState.data} />
        </Box>
      ) : null}

      <Typography sx={{ ...eyebrowSx(colors), mb: 0.75 }}>
        PO history · {fmtUnits(stats.pos)} POs · {fmtUnits(stats.units_ordered)} units ordered
        {stats.median_days_between_orders ? ` · reordered every ~${Math.round(stats.median_days_between_orders)} days` : ""}
        {stats.last_ordered ? ` · last ${fmtDay(stats.last_ordered)}` : ""}
      </Typography>
      {detail.history.length ? (
        <Box sx={{ overflowX: "auto", maxHeight: 380, overflowY: "auto" }}>
          <Table size="small" stickyHeader sx={{ minWidth: 860 }}>
            <TableHead>
              <TableRow>
                {HISTORY_COLUMNS.map((label) => (
                  <TableCell
                    key={label}
                    sx={headCellSx(ui, {
                      textAlign: ["Ordered", "Received", "Lead", "Late"].includes(label) ? "right" : "left",
                      whiteSpace: "nowrap",
                      backgroundColor: colors.bgPaper,
                    })}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.history.map((row) => (
                <TableRow key={`${row.po_id}-${row.warehouse_id}`} hover sx={{ cursor: "pointer" }} onClick={() => onOpenPo(row.po_id)}>
                  <TableCell sx={textCellSx(ui, { fontFamily: MONO_FONT, fontWeight: 600, whiteSpace: "nowrap" })}>{row.po_no}</TableCell>
                  <TableCell
                    sx={textCellSx(ui, {
                      whiteSpace: "nowrap",
                      color: row.warehouse_id === base.warehouse_id ? colors.textPrimary : colors.textTertiary,
                    })}
                  >
                    {row.warehouse_name || "Unmapped"}
                  </TableCell>
                  <TableCell sx={textCellSx(ui, { whiteSpace: "nowrap" })}>{fmtShortDay(row.po_date)}</TableCell>
                  <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>{fmtUnits(row.ordered)}</TableCell>
                  <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>
                    {row.received_reported === false ? (
                      <Box component="span" title="Received quantity not reported by the provider" sx={{ color: colors.textTertiary }}>—</Box>
                    ) : (
                      fmtUnits(row.received)
                    )}
                  </TableCell>
                  <TableCell sx={textCellSx(ui, { whiteSpace: "nowrap" })}>
                    {row.expected_date ? fmtShortDay(row.expected_date) : "—"}
                    {row.expected_kind === "dispatch" && row.expected_date ? (
                      <Box component="span" sx={{ fontSize: 10.5, color: colors.textTertiary }}> dispatch</Box>
                    ) : null}
                  </TableCell>
                  <TableCell sx={textCellSx(ui, { whiteSpace: "nowrap" })}>{row.received_date ? fmtShortDay(row.received_date) : "—"}</TableCell>
                  <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>{fmtLead(row.lead_days)}</TableCell>
                  <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT, color: row.days_late > 0 ? colors.textPrimary : colors.textTertiary, fontWeight: row.days_late > 7 ? 700 : 400 })}>
                    {row.days_late == null ? "—" : row.days_late > 0 ? `+${row.days_late}d` : `${row.days_late}d`}
                  </TableCell>
                  <TableCell sx={textCellSx(ui, { whiteSpace: "nowrap" })}>
                    {row.timing ? `${TIMING_LABEL[row.timing]}${row.days_overdue ? ` ${row.days_overdue}d` : ""}` : STAGE_LABEL[row.stage] || row.status}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>This SKU has never been on a purchase order.</Typography>
      )}
    </Box>
  );
};

export default SkuDetail;
